// noinspection JSUnusedGlobalSymbols
import type {ApiService} from "~/services/ApiService";
import type {
  ChangeEmailRequest,
  EnableMfaResponse,
  ForgotPasswordResponse,
  PermissionResponse,
  ResetPasswordResponse,
  SetPasswordRequest,
  User,
  AuthUser,
  ValidateEmailRequest
} from "~~/api/models";

export class UserService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  async login(email: string, password: string, headers = {}, rememberMe = false): Promise<{
    success: boolean,
    message: string,
    data: AuthUser,
    twoFactorRequired: boolean
  }> {
    return this.apiService.post("/api/user", {
      body: {email, password, rememberMe},
      headers: headers
    });
  }

  async loginmfa(email: string, mfacode: string, headers = {}, rememberMe = false): Promise<{
    success: boolean,
    message: string,
    data: AuthUser,
    twoFactorRequired: boolean
  }> {
    return this.apiService.post("/api/user/loginmfa", {
      body: {email: email, mfacode: mfacode, rememberMe},
      headers: headers
    });
  }

  async logout() {
    return this.apiService.post("/api/user/logout");
  }

  async validateResetToken(email: string, token: string): Promise<{
    success: boolean,
    message: string,
    data: boolean
  }> {
    return this.apiService.post("/api/user/validateresettoken", {
      body: {email, token},
    });
  }

  async resetPassword(email: string, password: string, confirmpassword: string, token: string): Promise<{
    success: boolean,
    message: string,
    data: ResetPasswordResponse[]
  }> {
    return this.apiService.post("/api/user/resetpassword", {
      body: {email, password, confirmpassword, token},
    });

  }

  async forgotPassword(email: string, returnLink: string): Promise<{
    success: boolean,
    message: ForgotPasswordResponse
  }> {
    return this.apiService.post("/api/user/forgotpassword", {
      body: {email, returnUrl: returnLink},
    });
  }

  async refreshUserToken(organisationId: string | null = null): Promise<{
    success: boolean,
    message: string,
    data: AuthUser
  }> {
    return this.apiService.get('/api/user/refresh', {
      query: {
        organisationId: organisationId
      }
    });
  }

  async enableTwoFactorAuth(): Promise<EnableMfaResponse> {
    return this.apiService.get("/api/user/enablemfa");
  }

  async activateTwoFactorAuth(mfaInfo: EnableMfaResponse): Promise<EnableMfaResponse> {
    return this.apiService.post('/api/user/activatemfa', {
      body: JSON.stringify(mfaInfo)
    });
  }

  async disableTwoFactorAuth(): Promise<boolean> {
    return this.apiService.delete('api/user/twofactor');
  }

  async fetchUser(): Promise<AuthUser> {
    return this.apiService.get('/api/user');
  }

  async changeLanguage(language: string): Promise<boolean> {
    return this.apiService.post('/api/user/changelanguage/' + language);
  }

  async getUserPermission(key: string): Promise<PermissionResponse> {
    return this.apiService.get(`/api/user/permissions/${key}`, {
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant?.toString() || '',
      }
    });
  }

  async getUserPermissions(keys: string[]): Promise<PermissionResponse[]> {
    return this.apiService.post(`/api/user/permissions`, {
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant?.toString() || '',
      },
      body: JSON.stringify(keys)
    });
  }

  async getAllowedPermissions(keys: string[]): Promise<PermissionResponse[]> {
    return this.apiService.post(`/api/user/permissions/allowed`, {
      body: JSON.stringify(keys)
    });
  }

  async changePassword(setPasswordRequest: SetPasswordRequest): Promise<boolean> {
    return this.apiService.put('/api/user/password', {
      body: JSON.stringify(setPasswordRequest)
    });
  }

  async updateUser(request: Partial<User>): Promise<User> {
    return this.apiService.put('/api/user', {
      body: JSON.stringify(request)
    });
  }

  async uploadAvatar(formData: FormData): Promise<User> {
    return this.apiService.put('/api/user/avatar', {
      body: formData
    });
  }

  async changeEmail(changeEmailRequest: ChangeEmailRequest): Promise<boolean> {
    return this.apiService.put('/api/user/email', {
      body: JSON.stringify(changeEmailRequest)
    });
  }

  async validateChangeEmail(userId: string, validateEmailRequest: ValidateEmailRequest): Promise<boolean> {
    return this.apiService.post(`/api/user/${userId}/validate-email`, {
      body: JSON.stringify(validateEmailRequest)
    });
  }
}